import React, { useEffect } from 'react';
import { MapPin, Clock, Train, Bus, Car, Accessibility, ExternalLink, Phone } from 'lucide-react';
import { Button } from './ui/button';
import { contactData } from '../data/mock';
import { PUBLIC_SITE_URL } from '../data/seo';
import { usePractice } from '../context/PracticeContext';
import { formatAddress, formatHours, mapsLinks, toPostalAddressLD, toGeoLD } from '../data/practice';

const GOOGLE_PLACE_ID = "ChIJQ5SgiDIFdkgRW3UZCS1yvqc";
const GOOGLE_MAPS_BUSINESS_URL =
  "https://www.google.com/maps/place/?q=place_id:" + GOOGLE_PLACE_ID;

const tubeStations = [
  {
    name: "Lambeth North",
    lines: "Bakerloo",
    walk: "≈ 1 min walk",
    badgeColors: ["#B36305"],
  },
  {
    name: "Waterloo",
    lines: "Bakerloo, Jubilee, Northern, Waterloo & City",
    walk: "≈ 7 min walk north",
    badgeColors: ["#B36305", "#A0A5A9", "#000000", "#95CDBA"],
  },
  {
    name: "Westminster",
    lines: "Jubilee, District, Circle",
    walk: "≈ 12 min walk west",
    badgeColors: ["#A0A5A9", "#00782A", "#FFD300"],
  },
];

const Location = () => {
  const practice = usePractice();
  const links = mapsLinks(practice);
  const fullAddress = formatAddress(practice);
  // The full MedicalClinic + LocalBusiness LD is emitted at the page level
  // from /app/frontend/src/data/structuredData.js. This stub keeps a
  // minimal Place reference that links to the canonical @id so the
  // section's anchor stays addressable in entity graphs.
  const localBusinessLD = {
    "@context": "https://schema.org",
    "@type": "Place",
    "@id": `${PUBLIC_SITE_URL}/#place`,
    "name": "The TMD Physio @ Waterloo",
    "url": `${PUBLIC_SITE_URL}/#location`,
    "address": toPostalAddressLD(practice),
    "geo": toGeoLD(practice),
    "hasMap": GOOGLE_MAPS_BUSINESS_URL,
    "containedInPlace": { "@id": `${PUBLIC_SITE_URL}/#clinic` },
  };

  // Imperative JSON-LD injection — same reason as SEO.jsx: React's
  // fiber deletion path can't handle <script> nodes in-tree during
  // route unmount.
  useEffect(() => {
    const id = 'location-localbusiness-jsonld';
    let el = document.getElementById(id);
    if (!el) {
      el = document.createElement('script');
      el.type = 'application/ld+json';
      el.id = id;
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(localBusinessLD);
    return () => {
      const node = document.getElementById(id);
      if (node && node.parentNode) node.parentNode.removeChild(node);
    };
  }, [JSON.stringify(localBusinessLD)]);

  return (
    <section id="location" className="location-section" data-testid="location-section">
      <div className="section-container">
        <div className="section-header">
          <h2 className="section-title" data-testid="visit-clinic-title">Visit Our Waterloo Clinic</h2>
          <p className="section-subtitle">
            Centrally located in Waterloo, on Westminster Bridge Road — a minute from Lambeth North and a short walk from Waterloo and Westminster.
          </p>
        </div>

        {/* MAP + ADDRESS PANEL */}
        <div className="location-main-grid">
          <div className="location-map-wrapper" data-testid="location-map">
            <iframe
              key={links.embed}
              src={links.embed}
              title={`Map showing The TMD Physio at ${fullAddress}`}
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
              className="location-map-iframe"
            />
          </div>

          <div className="location-info-panel">
            <div className="location-info-block">
              <div className="location-info-icon">
                <MapPin size={20} />
              </div>
              <div>
                <h3 className="location-info-heading">Address</h3>
                <p className="location-info-body" data-testid="location-address">
                  The TMD Physio<br />
                  {practice.address.street}<br />
                  {practice.address.city} {practice.address.postcode}<br />
                  {practice.address.country}
                </p>
                <p className="location-formerly" data-testid="location-formerly-note">
                  Previously located at Bedford Square, now based in Waterloo — same clinic, same team.
                </p>
              </div>
            </div>

            <div className="location-info-block">
              <div className="location-info-icon">
                <Clock size={20} />
              </div>
              <div>
                <h3 className="location-info-heading">Opening Hours</h3>
                {practice.hours.map((h) => (
                  <p key={h.day} className="location-info-body" data-testid={`location-hours-${h.day.toLowerCase()}`}>
                    <strong>{h.day}:</strong> {formatHours(h)}
                  </p>
                ))}
              </div>
            </div>

            <div className="location-info-block">
              <div className="location-info-icon">
                <Phone size={20} />
              </div>
              <div>
                <h3 className="location-info-heading">Contact</h3>
                <p className="location-info-body">
                  <a href={`tel:${contactData.phone.replace(/\s/g, '')}`} className="location-info-link" data-testid="location-phone-link">
                    {contactData.phone}
                  </a>
                  <br />
                  <a href={`mailto:${contactData.email}`} className="location-info-link" data-testid="location-email-link">
                    {contactData.email}
                  </a>
                </p>
              </div>
            </div>

            <div className="location-cta-row">
              <a
                href={links.directions}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="get-directions-btn"
              >
                <Button className="cta-primary location-cta-primary">
                  <MapPin size={18} />
                  Get Directions
                </Button>
              </a>
              <a
                href={GOOGLE_MAPS_BUSINESS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="location-secondary-link"
                data-testid="view-on-google-btn"
              >
                View on Google Maps
                <ExternalLink size={14} />
              </a>
            </div>
          </div>
        </div>

        {/* GETTING HERE GRID */}
        <div className="location-getting-here">
          <div className="getting-here-card" data-testid="getting-here-tube">
            <div className="getting-here-header">
              <div className="getting-here-icon tube">
                <Train size={20} />
              </div>
              <h3 className="getting-here-title">By Underground</h3>
            </div>
            <ul className="tube-station-list">
              {tubeStations.map((station) => (
                <li key={station.name} className="tube-station-item">
                  <div className="tube-station-lines" aria-hidden="true">
                    {station.badgeColors.map((color, idx) => (
                      <span key={`${station.name}-line-${idx}`} className="tube-line-dot" style={{ background: color }} />
                    ))}
                  </div>
                  <div className="tube-station-details">
                    <div className="tube-station-name">{station.name}</div>
                    <div className="tube-station-meta">
                      <span className="tube-station-line-label">{station.lines}</span>
                      <span className="tube-station-walk">• {station.walk}</span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
            <a
              href="https://tfl.gov.uk/plan-a-journey/"
              target="_blank"
              rel="noopener noreferrer"
              className="getting-here-link"
            >
              Plan your journey on TfL <ExternalLink size={12} />
            </a>

            <a
              href={links.citymapper}
              target="_blank"
              rel="noopener noreferrer"
              className="citymapper-btn"
              data-testid="citymapper-btn"
              aria-label="Get directions from your current location with Citymapper"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.94-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
              </svg>
              Open in Citymapper
            </a>
          </div>

          <div className="getting-here-card" data-testid="getting-here-bus-car">
            <div className="getting-here-header">
              <div className="getting-here-icon bus">
                <Bus size={20} />
              </div>
              <h3 className="getting-here-title">By Bus & Car</h3>
            </div>
            <div className="getting-here-body">
              <p>
                <strong>Buses:</strong> Routes <strong>12, 53, 59, 76, 148, 159, 453</strong> stop at Lambeth North Station and Lower Marsh, all under 3 minutes walk.
              </p>
              <p>
                <Car size={14} style={{ display: 'inline', verticalAlign: '-2px', marginRight: '4px' }} />
                <strong>By car:</strong> Westminster Bridge Road sits within Lambeth&apos;s Waterloo Controlled Parking Zone, so on-street parking is limited — check the street signs for restriction hours. Nearest car parks: <strong>Britannia Parking, Upper Marsh</strong> and <strong>APCOA Cornwall Road, Waterloo</strong>.
              </p>
              <p>
                <strong>Congestion Charge:</strong> The clinic is inside the Congestion Charge Zone and ULEZ — check current TfL charges before travelling.
              </p>
            </div>
          </div>

          <div className="getting-here-card" data-testid="getting-here-accessibility">
            <div className="getting-here-header">
              <div className="getting-here-icon access">
                <Accessibility size={20} />
              </div>
              <h3 className="getting-here-title">Accessibility</h3>
            </div>
            <div className="getting-here-body">
              <p>
                We&apos;re committed to making the clinic welcoming for everyone. If you have specific accessibility needs — step-free access, hearing-loop support, larger-print materials, a chaperone, or anything else — please <strong>contact us in advance of your appointment</strong> so we can prepare.
              </p>
              <p>
                <strong>Nearby step-free Tube stations:</strong> Waterloo and Westminster (Jubilee line) are the closest step-free stations; Lambeth North is not step-free.
              </p>
              <a
                href={`mailto:${contactData.email}?subject=Accessibility%20question`}
                className="getting-here-link"
              >
                Email us about accessibility <ExternalLink size={12} />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Location;
